import { useMemo, useState } from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/clientWeb/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/clientWeb/components/ui/command"
import { cn } from "@/clientWeb/components/lib/utils"
import { useLanguage } from "@/clientWeb/i18n/LanguageContext"
import { dialCodeOf, phoneCountries, type CountryCode } from "../utils/phone"

interface PhoneCountrySelectProps {
  value: CountryCode
  onChange: (country: CountryCode) => void
  disabled?: boolean
  className?: string
}

/**
 * The country-code dropdown in front of the profile phone. Searchable by
 * country name, ISO code or calling code ("india", "IN", "+91", "91"), since
 * the list holds every country the phone rules know.
 *
 * The trigger shows the code, not a flag: Windows renders flag emoji as two
 * letters, and the code is what the user needs to see beside the number.
 */
export function PhoneCountrySelect({ value, onChange, disabled = false, className }: PhoneCountrySelectProps) {
  const { language, t } = useLanguage()
  const [open, setOpen] = useState(false)
  const countries = useMemo(() => phoneCountries(language), [language])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={t("profile.phoneCountry")}
          disabled={disabled}
          className={cn(
            "flex h-10 shrink-0 items-center gap-1 rounded-md border border-input bg-card px-3 text-sm hover:bg-accent/30 transition-colors",
            disabled && "opacity-50 cursor-not-allowed",
            className,
          )}
        >
          <span className="text-muted-foreground text-xs">{value}</span>
          <span className="text-foreground">{dialCodeOf(value)}</span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 text-muted-foreground opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(320px,calc(100vw-2rem))] p-0">
        <Command>
          <CommandInput placeholder={t("profile.phoneCountrySearch")} />
          <CommandList>
            <CommandEmpty>{t("profile.phoneCountryNone")}</CommandEmpty>
            <CommandGroup>
              {countries.map((c) => (
                <CommandItem
                  key={c.iso}
                  value={`${c.name} ${c.iso} ${c.dialCode} ${c.dialCode.slice(1)}`}
                  onSelect={() => {
                    onChange(c.iso)
                    setOpen(false)
                  }}
                >
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{c.dialCode}</span>
                  <Check className={cn("w-4 h-4 shrink-0", value === c.iso ? "opacity-100 text-primary" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
